import type { CoreComponent } from '../../../types/forge';
import { useEditorStore } from '../store/editorStore';

/**
 * Hard-coded group display order. The CoreComponent query orders by
 * `display_name`, which causes Map insertion order to reflect "first
 * alphabetical component name in each group" — useful for nothing.
 *
 * `Navigation` is intentionally first: setting up Screens / Stacks /
 * Tabs is the primary mental model authors start from when scaffolding
 * a new app, so it should be the first thing they see in the palette.
 *
 * Unknown / future groups fall to the end in their natural order.
 */
const GROUP_ORDER: readonly string[] = [
  'Navigation',
  'Layout',
  'Text',
  'Forms',
  'Media',
];

export function ComponentPalette({ palette }: { palette: { CoreComponent: CoreComponent[] } }) {
  const startDrag = useEditorStore((s) => s.startDrag);
  const endDrag = useEditorStore((s) => s.endDrag);

  const grouped: Record<string, CoreComponent[]> = {};
  for (const c of palette?.CoreComponent ?? []) {
    const g = c.palette_group ?? 'Other';
    (grouped[g] ??= []).push(c);
  }

  const orderedGroups = Object.entries(grouped).sort(([a], [b]) => {
    const ia = GROUP_ORDER.indexOf(a);
    const ib = GROUP_ORDER.indexOf(b);
    // Known groups come first in GROUP_ORDER order; unknown groups fall
    // to the end in their alphabetical / insertion order.
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <section style={styles.section}>
      <h3 style={styles.heading}>Components</h3>
      {orderedGroups.map(([group, items]) => (
        <div key={group} style={styles.group}>
          <div style={styles.groupName}>{group}</div>
          <div style={styles.tiles}>
            {items.map((c) => (
              <div
                key={c.id}
                draggable
                onDragStart={() => startDrag(c.type)}
                onDragEnd={endDrag}
                style={styles.tile}
                title={c.type}
              >
                {c.display_name}
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { padding: 12, borderBottom: '1px solid #E2E8F0' },
  heading: { fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' },
  group: { marginBottom: 12 },
  groupName: { fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  tiles: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },
  tile: {
    padding: '8px 10px', fontSize: 12, fontWeight: 500,
    backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0',
    borderRadius: 6, cursor: 'grab', userSelect: 'none', color: '#475569',
  },
};
